import * as Yup from 'yup';
import { checkVAT, countries } from 'jsvat';

const notDiscounted = (discount: any) =>
  !discount || (discount?.percentOff === 0 && discount?.amountOff === 0);
const notFullyDiscounted = (discount: any) => !discount || discount?.percentOff !== 100;

const basicFields = {
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  email: Yup.string().email("This email address isn't valid").required('Required'),

  password: Yup.string()
    .required('Required')
    .matches(/.*[a-z].*/, 'Needs at least one lowercase character')
    .matches(/.*[A-Z].*/, 'Needs at least one uppercase character')
    .matches(/.*[0-9].*/, 'Needs at least one number')
    .min(8, 'Minimum 8 characters'),

  acceptTerms: Yup.bool().oneOf([true], 'You must agree to these'),

  // Promo codes
  usePromoCode: Yup.boolean().required(),
  promoCode: Yup.string().when('usePromoCode', {
    is: true,
    then: schema =>
      schema.required('Missing promo code (uncheck the box to remove)').when('discount', {
        is: notDiscounted,
        // Workaround to force field error if discount isn't valid
        then: sch => sch.max(0, 'Promo code is not valid'),
      }),
  }),
};

const paymentFields = {
  // Payment
  plan: Yup.string().oneOf(['monthly', 'yearly']),
  paymentElementComplete: Yup.boolean().when('discount', {
    is: notFullyDiscounted,
    then: schema => schema.required('required').oneOf([true], 'Please fill in all the card fields'),
  }),

  // VAT
  vatNumber: Yup.string().when('showVatFields', {
    is: true,
    then: schema =>
      schema.required('Required for VAT ID').test({
        name: 'Valid VAT Number',
        test: (value: string | undefined) => {
          return value !== undefined && checkVAT(value, countries).isValid;
        },
        message: 'Invalid VAT number',
      }),
  }),

  // Location
  isEuropeanUnion: Yup.boolean().required(),
  country: Yup.string().required(),
  isUAE: Yup.boolean().isFalse(),

  postalCode: Yup.string().when('showVatFields', {
    is: true,
    then: schema => schema.notRequired(),
    otherwise: showVatFieldSchema =>
      showVatFieldSchema.when('discount', {
        is: notFullyDiscounted,
        then: discountSchema =>
          discountSchema.when('country', {
            is: (country: string) => country === 'US' || country === 'CA' || country === 'GB',
            then: countrySchema =>
              countrySchema
                .required('Required')
                .when('country', {
                  is: 'US',
                  then: s =>
                    s
                      .matches(/^[0-9]+$/, 'Must be a valid US zip code')
                      .min(5, 'Must a valid US zip code')
                      .max(5, 'Must a valid US zip code'),
                })
                .when('country', {
                  is: 'CA',
                  then: s =>
                    s.matches(
                      /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i,
                      'Must be a valid Canadian postal code',
                    ),
                })
                .when('country', {
                  is: 'GB',
                  then: s =>
                    s.matches(
                      /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i,
                      'Must be a valid UK postal code',
                    ),
                }),
          }),
      }),
  }),

  // Address
  address: Yup.object().when('showVatFields', {
    is: true,
    then: schema =>
      schema
        .shape({
          complete: Yup.boolean().oneOf([true], 'Please fill your name and all the address fields'),
        })
        .required('Required'),
  }),
};

const signupFormSchema = Yup.object().shape({
  ...basicFields,
  ...paymentFields,
});

export default signupFormSchema;
